import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { PersonSearchResult } from '../types';
import { PersonService } from '../services/personService';
import { SearchResultsList } from '../components/search/SearchResultsList';
import { SearchFiltersPanel } from '../components/search/SearchFiltersPanel';
import { NoResults } from '../components/search/NoResults';
import { PeopleFilters } from '../hooks/usePeopleFilters';

export type SortOption = 'relevance' | 'nameAsc' | 'nameDesc' | 'birthDateAsc' | 'birthDateDesc';

/**
 * Search Results Page
 *
 * Dedicated search results page for comprehensive search
 *
 * URL: /search?q={term}&filters={...}
 *
 * Features:
 * - Display search results from GlobalSearchBar
 * - Apply filters (same as People page)
 * - Sort results by relevance, name, birth date
 * - Pagination for large result sets
 * - Recent searches (localStorage)
 * - No results state with suggestions
 *
 * @component
 * @returns {JSX.Element} Search results page
 */
export const Search: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  // ===== State =====
  const [searchResults, setSearchResults] = useState<PersonSearchResult[]>([]);
  const [filteredResults, setFilteredResults] = useState<PersonSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<SortOption>('relevance');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<PeopleFilters>({});

  // Get search query from URL
  const query = searchParams.get('q') || '';

  // ===== Effects =====
  useEffect(() => {
    if (query.trim()) {
      performSearch(query.trim());
      saveRecentSearch(query.trim());
    } else {
      setSearchResults([]);
      setFilteredResults([]);
    }
  }, [query]);

  useEffect(() => {
    // Apply filters and sorting
    let results = [...searchResults];

    // Apply filters
    if (filters.gender) {
      // Note: PersonSearchResult doesn't have gender, so this won't filter
      // We're keeping the filter UI for consistency but it won't affect results
    }

    if (filters.isDeceased !== undefined) {
      results = results.filter((person) => {
        const isDeceased = !!person.deathDate;
        return isDeceased === filters.isDeceased;
      });
    }

    if (filters.birthYearFrom !== undefined || filters.birthYearTo !== undefined) {
      results = results.filter((person) => {
        if (!person.birthDate) return false;
        const birthYear = new Date(person.birthDate).getFullYear();

        if (filters.birthYearFrom !== undefined && birthYear < filters.birthYearFrom) {
          return false;
        }

        if (filters.birthYearTo !== undefined && birthYear > filters.birthYearTo) {
          return false;
        }

        return true;
      });
    }

    // Apply sorting
    const sorted = sortResults(results, sortOption);
    setFilteredResults(sorted);
  }, [searchResults, filters, sortOption]);

  // ===== Search Logic =====
  const performSearch = async (searchTerm: string) => {
    try {
      setLoading(true);
      setError(null);

      // Use PersonService.searchPersons() - already implemented!
      const results = await PersonService.searchPersons(searchTerm, {});

      setSearchResults(results);
    } catch (err: any) {
      console.error('Search failed:', err);
      setError('Failed to search. Please try again.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const sortResults = (results: PersonSearchResult[], sort: SortOption): PersonSearchResult[] => {
    const sorted = [...results];

    switch (sort) {
      case 'relevance':
        // Sort by relevance score (already in results)
        return sorted.sort((a, b) => b.relevanceScore - a.relevanceScore);
      case 'nameAsc':
        return sorted.sort((a, b) => {
          const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
          const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
      case 'nameDesc':
        return sorted.sort((a, b) => {
          const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
          const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
          return nameB.localeCompare(nameA);
        });
      case 'birthDateAsc':
        return sorted.sort((a, b) => {
          if (!a.birthDate) return 1;
          if (!b.birthDate) return -1;
          return a.birthDate.localeCompare(b.birthDate);
        });
      case 'birthDateDesc':
        return sorted.sort((a, b) => {
          if (!a.birthDate) return 1;
          if (!b.birthDate) return -1;
          return b.birthDate.localeCompare(a.birthDate);
        });
      default:
        return sorted;
    }
  };

  const saveRecentSearch = (searchTerm: string) => {
    try {
      const recentSearches = JSON.parse(localStorage.getItem('recentSearches') || '[]');

      // Add to beginning, remove duplicates, limit to 10
      const updated = [
        searchTerm,
        ...recentSearches.filter((s: string) => s !== searchTerm),
      ].slice(0, 10);

      localStorage.setItem('recentSearches', JSON.stringify(updated));
    } catch (err) {
      console.error('Failed to save recent search:', err);
    }
  };

  // ===== Event Handlers =====
  const handleSortChange = (newSort: SortOption) => {
    setSortOption(newSort);
  };

  const handleFilterToggle = () => {
    setFilterDrawerOpen(!filterDrawerOpen);
  };

  const handleFilterChange = (newFilters: PeopleFilters) => {
    setFilters(newFilters);
  };

  const handleFilterRemove = (filterKey: string) => {
    setFilters((prev) => {
      const updated = { ...prev };
      delete updated[filterKey as keyof PeopleFilters];
      return updated;
    });
  };

  const handleClearAllFilters = () => {
    setFilters({});
  };

  // ===== Helper Functions =====
  const getFilterLabel = (key: string, value: any): string => {
    switch (key) {
      case 'gender':
        return `Gender: ${value}`;
      case 'isDeceased':
        return value ? 'Deceased' : 'Living';
      case 'birthYearFrom':
        return `Born after ${value}`;
      case 'birthYearTo':
        return `Born before ${value}`;
      case 'hasPhoto':
        return value ? 'Has photo' : 'No photo';
      case 'hasBiography':
        return value ? 'Has biography' : 'No biography';
      case 'lastName':
        return `Last name: ${value}`;
      default:
        return '';
    }
  };

  // ===== Computed Values =====
  const hasResults = filteredResults.length > 0;
  const showNoResults = !loading && !error && query && !hasResults;
  const displayPersons = filteredResults;
  const activeFilterCount = Object.keys(filters).length;

  // ===== Render =====
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Search Results
        </Typography>

        {query && (
          <Typography variant="body1" color="text.secondary">
            {loading ? 'Searching...' : `${filteredResults.length} result${filteredResults.length !== 1 ? 's' : ''} for "${query}"`}
          </Typography>
        )}
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters and Sort Controls */}
      {query && !loading && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            sx={{ gap: 1 }}
          >
            {/* Filter Toggle Button */}
            <Button
              variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon />}
              onClick={handleFilterToggle}
              size="small"
            >
              Filters
              {activeFilterCount > 0 && ` (${activeFilterCount})`}
            </Button>

            {/* Active Filter Chips */}
            {Object.entries(filters).map(([key, value]) => {
              const label = getFilterLabel(key, value);
              if (!label) return null;

              return (
                <Chip
                  key={key}
                  label={label}
                  onDelete={() => handleFilterRemove(key)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              );
            })}

            {/* Clear All Filters */}
            {activeFilterCount > 0 && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearAllFilters}
              >
                Clear All
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Filter Drawer (Desktop: Sidebar, Mobile: Drawer) */}
        <SearchFiltersPanel
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          filters={filters}
          onFiltersChange={handleFilterChange}
          isMobile={isMobile}
        />

        {/* Results Area */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {/* No Results State */}
          {showNoResults && <NoResults query={query} />}

          {/* Results List */}
          {!loading && !showNoResults && hasResults && (
            <SearchResultsList
              results={displayPersons}
              sortOption={sortOption}
              onSortChange={handleSortChange}
            />
          )}

          {/* No Query State */}
          {!query && !loading && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Enter a search term to get started
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use the search bar in the header to find people in your family tree
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </Container>
  );
};
